// Where every unexpected error ends up: the console, with one prefix to filter on, and a short ring
// buffer in sessionStorage so a crash report can carry what happened just before.

const KEY = 'papurr:errors';
const KEEP = 20;
/** Logged once by a browser after layout loops; harmless, and not ours */
const BENIGN = /ResizeObserver loop/;

export type ErrorRecord = { at: string; where: string; message: string; stack?: string; extra?: string };

function describe(e: unknown): { message: string; stack?: string } {
  if (e instanceof Error) return { message: `${e.name}: ${e.message}`, stack: e.stack };
  return { message: typeof e === 'string' ? e : JSON.stringify(e) };
}

/** Records an error; `extra` is context such as React's component stack */
export function reportError(where: string, e: unknown, extra?: string): ErrorRecord {
  const rec: ErrorRecord = { at: new Date().toISOString(), where, ...describe(e), extra };
  console.error(`[papurr] ${where}:`, e, extra ?? '');
  try {
    const list = recentErrors();
    list.push(rec);
    sessionStorage.setItem(KEY, JSON.stringify(list.slice(-KEEP)));
  } catch {
    // storage blocked: the console still has it
  }
  return rec;
}

export function recentErrors(): ErrorRecord[] {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) ?? '[]') as ErrorRecord[];
  } catch {
    return [];
  }
}

/** Text for a bug report: the error, where it happened, what came before, and the environment */
export function crashReport(rec: ErrorRecord): string {
  const before = recentErrors().filter((r) => r !== rec && r.at !== rec.at);
  return [
    `${rec.where}: ${rec.message}`,
    rec.stack ?? '',
    rec.extra ? `--- components ---${rec.extra}` : '',
    before.length ? `--- earlier ---\n${before.map((r) => `${r.at} ${r.where}: ${r.message}`).join('\n')}` : '',
    `--- ${location.href} · ${navigator.userAgent} · ${rec.at}`,
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * Catches what React cannot: errors thrown outside rendering (timers, sockets, promises). They do not
 * blank the app, but they are the first clue when something stops updating.
 */
export function installGlobalErrorLog(): void {
  window.addEventListener('error', (e) => {
    if (BENIGN.test(e.message)) return;
    // React reports uncaught render errors here too (already recorded by the boundary): keep one entry
    if (e.error && (e.error as { __papurrSeen?: boolean }).__papurrSeen) return;
    reportError('window', e.error ?? e.message);
  });
  window.addEventListener('unhandledrejection', (e) => reportError('promise', e.reason));
}
